import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { brand } from '@iheart/shared';
import { MotionButton, GradientText } from '@iheart/ui';
import { api, ApiError } from '../lib/api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { PublicFooter, PublicNav } from '../components/PublicFooter';

export function ResetPasswordRoute() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // Arriving from a team invitation — reword the page as "accept & set password"
  // (the same secure token flow, just friendlier for a first-time user).
  const isInvite = searchParams.get('invite') === '1';
  useDocumentTitle(isInvite ? 'Accept Invitation' : 'Reset Password');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const canSubmit = newPassword.length >= 8 && newPassword === confirmPassword && !!token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: { token, newPassword },
      });
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setError(body?.message ?? 'Failed to reset password. The link may have expired.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Password strength
  const getStrength = (pw: string): { label: string; color: string; width: string } => {
    if (pw.length === 0) return { label: '', color: 'bg-neutral-200', width: '0%' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: 'Weak', color: 'bg-red-500', width: '20%' };
    if (score <= 2) return { label: 'Fair', color: 'bg-orange-400', width: '40%' };
    if (score <= 3) return { label: 'Good', color: 'bg-blue-500', width: '60%' };
    if (score <= 4) return { label: 'Strong', color: 'bg-emerald-500', width: '80%' };
    return { label: 'Very strong', color: 'bg-emerald-600', width: '100%' };
  };

  const strength = getStrength(newPassword);

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12 pt-20">
      <PublicNav />
      <div
        className="pointer-events-none absolute -left-40 -top-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -right-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />

      <div className="relative w-full max-w-sm rounded-2xl border border-white/60 bg-white/80 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl">
        {!token ? (
          /* No token in URL */
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-amber-50">
              <AlertTriangle className="size-6 text-amber-500" />
            </div>
            <h1 className="text-lg font-semibold text-neutral-800">Invalid reset link</h1>
            <p className="text-sm text-neutral-500">
              This link appears to be invalid or incomplete. Please request a new password reset.
            </p>
            <Link
              to="/auth/forgot-password"
              className="mt-2 text-sm font-medium text-pink-600 transition-colors hover:text-pink-700"
            >
              Request a new reset link
            </Link>
          </div>
        ) : success ? (
          /* Success state */
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="size-6 text-emerald-500" />
            </div>
            <h1 className="text-lg font-semibold text-neutral-800">{isInvite ? 'Welcome aboard!' : 'Password reset!'}</h1>
            <p className="text-sm text-neutral-500">
              {isInvite
                ? 'Your account is ready. Sign in to get started.'
                : 'Your password has been updated successfully. You can now sign in with your new password.'}
            </p>
            <Link
              to="/login"
              className="mt-3"
            >
              <MotionButton size="lg">
                Sign in
              </MotionButton>
            </Link>
          </div>
        ) : (
          /* Reset form */
          <>
            <Link
              to="/login"
              className="mb-4 inline-flex items-center gap-1 text-xs text-neutral-500 transition-colors hover:text-neutral-700"
            >
              <ArrowLeft className="size-3.5" /> Back to sign in
            </Link>

            <div className="mb-6 flex flex-col items-center gap-3 text-center">
              <div
                className="flex size-12 items-center justify-center rounded-full"
                style={{ backgroundImage: brand.gradient.primary }}
              >
                <Lock className="size-6 text-white" />
              </div>
              <h1 className="text-xl font-semibold">
                <GradientText>{isInvite ? 'Accept your invitation' : 'Set New Password'}</GradientText>
              </h1>
              <p className="text-sm text-neutral-500">
                {isInvite
                  ? 'Choose a password to finish setting up your account.'
                  : 'Choose a strong password with at least 8 characters.'}
              </p>
            </div>

            <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="new-password" className="mb-1 block text-sm font-medium text-neutral-700">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2.5 pr-10 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-400 transition-colors hover:text-neutral-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
                {tooShort && (
                  <p className="mt-1 text-xs text-amber-600">Minimum 8 characters</p>
                )}
                {/* Strength bar */}
                {newPassword.length > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 w-full rounded-full bg-neutral-100">
                      <div
                        className={`h-full rounded-full transition-all ${strength.color}`}
                        style={{ width: strength.width }}
                      />
                    </div>
                    <p className="mt-0.5 text-[10px] text-neutral-400">{strength.label}</p>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirm-password" className="mb-1 block text-sm font-medium text-neutral-700">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                />
                {mismatch && (
                  <p className="mt-1 text-xs text-red-600">Passwords don't match</p>
                )}
              </div>

              {error && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </div>
              )}

              <MotionButton
                type="submit"
                size="lg"
                className="mt-1 w-full"
                loading={submitting}
                disabled={!canSubmit}
              >
                {submitting ? (isInvite ? 'Setting up…' : 'Resetting...') : isInvite ? 'Accept & set password' : 'Reset password'}
              </MotionButton>
            </form>
          </>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
